/**
 * The workspace side of Companies + approvals (ADR-022), rendered under a
 * shared (org) workspace in Workspace Controls:
 *
 *   - CompanyCard: which Company holds this workspace, or every open
 *     proposal (one per Company) — which the OWNER can accept having read
 *     who proposed it, who pays, the named Company owners the "add as
 *     admins" checkbox would seat, and the Company's approvals that apply on
 *     joining — and a plain statement that only the Company can unlink.
 *   - ApprovalsCard: which arches may work with this workspace's data, with
 *     origin (here / the Company), growth since approval (and re-approval
 *     when it needs one), revoke, what each arche has read, and who was
 *     refused.
 */

import { useId, useState } from 'react';
import { Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { api, type WorkspaceCompanyDto, type WorkspacePendingCompanyLinkDto } from '@/lib/api';
import {
  useAcceptCompanyLink,
  useDeclineCompanyLink,
  useDecideWorkspaceApproval,
  useRevokeWorkspaceApproval,
  useWorkspaceAccessDenials,
  useWorkspaceApprovals,
  useWorkspaceCompany,
} from '../hooks/useCompanies';
import { ArcheApprovalDialog } from './ArcheApprovalDialog';
import { ApprovalsList } from './ApprovalsList';
import { appAccessSectionPath } from '../pages/app-access/shared';
import { companyErrorCode, describeCompanyError } from '../lib/company-errors';

/** A person the accept screen names: display name or @username, else a short id. */
function personLabel(p: { label: string | null; principalId?: string; userId?: string | null }) {
  if (p.label) return p.label;
  const id = p.principalId ?? p.userId ?? '';
  return `someone (…${id.slice(-6)})`;
}

/** "A", "A and B", "A, B and C". */
function listNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * One open Company link proposal. A workspace OWNER is shown everything
 * accepting lets in — who proposed it, who will pay, the NAMED Company
 * owners the "add as admins" checkbox would seat, and every arche the
 * Company has approved with the reach it was approved for (all of which
 * apply the moment the workspace joins) — before Accept. Others see that a
 * proposal is pending.
 *
 * The accept carries the owners that were shown (`expectedOwnerPrincipalIds`)
 * so a change to the Company's owners after the screen was drawn is refused
 * (COMPANY_OWNERS_CHANGED) rather than seating someone nobody saw here.
 */
export function CompanyLinkProposalPanel({
  holderId,
  proposal,
  workspaceName,
}: {
  holderId: string;
  proposal: WorkspacePendingCompanyLinkDto;
  /** Named when the panel is shown away from the workspace itself (the banner). */
  workspaceName?: string;
}) {
  const accept = useAcceptCompanyLink(holderId);
  const decline = useDeclineCompanyLink(holderId);
  const [addOwners, setAddOwners] = useState(false);
  const [declining, setDeclining] = useState(false);
  const checkboxId = useId();
  const { invitationId, companyName, expiresAt, canAccept } = proposal;
  const owners = proposal.companyOwners ?? [];
  const ownerNames = listNames(owners.map(personLabel));
  const approvals = proposal.companyApprovals ?? [];
  const here = workspaceName ?? 'this workspace';

  async function handleAccept(): Promise<void> {
    try {
      const r = await accept.mutateAsync({
        invitationId,
        addOwnersAsAdmins: addOwners,
        // Exactly the owners named above; the server refuses if they changed.
        expectedOwnerPrincipalIds: addOwners ? owners.map((o) => o.principalId) : undefined,
      });
      const notes: string[] = [];
      if (r.ownersSeated.length > 0) {
        notes.push(`${r.ownersSeated.length} Company owner(s) added as admins.`);
      }
      if (r.ownersAlreadyMembers.length > 0) {
        notes.push(`${r.ownersAlreadyMembers.length} already on the roster, role unchanged.`);
      }
      toast({
        title: `Linked ${here} to ${companyName}`,
        description: notes.length > 0 ? notes.join(' ') : undefined,
      });
    } catch (err) {
      if (companyErrorCode(err) === 'COMPANY_OWNERS_CHANGED') {
        // The panel is re-read with the owners as they are now; the box is
        // cleared so seating them is a fresh, informed choice.
        setAddOwners(false);
        toast({
          title: `${companyName}’s owners changed`,
          description: 'Review the owners named now, then accept again.',
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'Could not accept',
        description: describeCompanyError(err),
        variant: 'destructive',
      });
    }
  }

  async function handleDecline(): Promise<void> {
    try {
      await decline.mutateAsync(invitationId);
      setDeclining(false);
      toast({ title: `Declined ${companyName}’s invitation` });
    } catch (err) {
      setDeclining(false);
      toast({
        title: 'Could not decline',
        description: describeCompanyError(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <div className="space-y-3 rounded-md border border-primary/40 bg-primary/5 p-3 text-sm">
      <p className="text-foreground">
        <span className="font-medium">{companyName}</span> invites{' '}
        {workspaceName ? <span className="font-medium">{workspaceName}</span> : 'this workspace'} to
        join it.
      </p>
      {canAccept && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          {proposal.proposedBy && (
            <>
              <dt className="text-muted-foreground">Proposed by</dt>
              <dd className="text-foreground">{personLabel(proposal.proposedBy)}</dd>
            </>
          )}
          {proposal.payer && (
            <>
              <dt className="text-muted-foreground">Would pay</dt>
              <dd className="text-foreground">{personLabel(proposal.payer)}</dd>
            </>
          )}
          {owners.length > 0 && (
            <>
              <dt className="text-muted-foreground">Company owners</dt>
              <dd className="text-foreground">{ownerNames}</dd>
            </>
          )}
        </dl>
      )}
      <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
        <li>
          Billing for this workspace moves to the Company&apos;s payer
          {proposal.payer ? ` (${personLabel(proposal.payer)})` : ''} immediately — no proration.
        </li>
        <li>Only the Company can unlink it afterwards.</li>
        <li>
          Arches the Company approves can read this workspace&apos;s data on behalf of its members,
          at each member&apos;s own role. You can deny any arche here.
        </li>
        <li>
          {canAccept && addOwners ? (
            <>
              <span className="text-foreground">{ownerNames}</span> become admins of this workspace
              — they can see its confidential files — until they stop being owners of {companyName}{' '}
              or this workspace leaves it. Nothing else changes.
            </>
          ) : (
            'Nobody is added to the roster: same people, same roles, same data.'
          )}
        </li>
      </ul>
      {canAccept && (
        <div className="space-y-1">
          <p className="font-medium text-foreground">
            Applies the moment it joins — {companyName}&apos;s arche approvals
          </p>
          {approvals.length === 0 ? (
            <p className="text-muted-foreground">{companyName} has approved no arches.</p>
          ) : (
            <ul className="space-y-1">
              {approvals.map((a) => (
                <li key={a.archeId}>
                  <span className="text-foreground">
                    {a.archeName ?? `Arche …${a.archeId.slice(-6)}`}
                  </span>
                  {a.reach.length === 0 ? (
                    <span className="text-muted-foreground">
                      {' '}
                      — nothing beyond what it already can.
                    </span>
                  ) : (
                    <ul className="list-disc pl-5 text-muted-foreground">
                      {a.reach.map((sentence) => (
                        <li key={sentence}>{sentence}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="text-muted-foreground">
            {proposal.companyTrustsFias
              ? 'It trusts Fias arches: the platform’s own first-party arches, from a fixed reviewed list, are approved too.'
              : 'It does not trust Fias arches.'}
          </p>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        This invitation lapses on {new Date(expiresAt).toLocaleDateString()}.
      </p>
      {canAccept ? (
        <>
          <div className="flex items-start gap-2">
            <Checkbox
              id={checkboxId}
              checked={addOwners}
              onCheckedChange={(v) => setAddOwners(v === true)}
              disabled={owners.length === 0}
            />
            <Label htmlFor={checkboxId} className="text-sm leading-tight">
              Also add this Company&apos;s owners as admins of this workspace
              {owners.length > 0 ? `: ${ownerNames}` : ''}
            </Label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void handleAccept()} disabled={accept.isPending}>
              {accept.isPending ? 'Linking…' : 'Accept'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDeclining(true)}
              disabled={decline.isPending}
            >
              Decline
            </Button>
          </div>
          <AlertDialog open={declining} onOpenChange={(o) => !decline.isPending && setDeclining(o)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Decline {companyName}’s invitation?</AlertDialogTitle>
                <AlertDialogDescription>
                  Nothing changes for {here}. If you change your mind, the Company has to invite it
                  again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={decline.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={decline.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    void handleDecline();
                  }}
                >
                  {decline.isPending ? 'Declining…' : 'Decline'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : (
        <p className="text-muted-foreground">Only a workspace owner can accept.</p>
      )}
    </div>
  );
}

/**
 * One proposal looked up by id on its workspace — for the Workspace Controls
 * banner, whose feed row does not carry what an owner must see before
 * accepting. Renders nothing once the proposal is no longer open.
 */
export function WorkspaceCompanyProposal({
  holderId,
  invitationId,
  workspaceName,
}: {
  holderId: string;
  invitationId: string;
  workspaceName: string;
}) {
  const query = useWorkspaceCompany(holderId);
  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (query.isError) {
    return <p className="text-sm text-destructive">{describeCompanyError(query.error)}</p>;
  }
  const proposal = query.data?.pendingLinks.find((p) => p.invitationId === invitationId);
  if (!proposal) return null;
  return (
    <CompanyLinkProposalPanel
      holderId={holderId}
      proposal={proposal}
      workspaceName={workspaceName}
    />
  );
}

/** Anchor the Workspace Controls banner scrolls to ("Open workspace"). */
export const WORKSPACE_COMPANY_CARD_ID = 'workspace-company-card';

const EMAIL_REACH: Record<'workspace' | 'company' | 'any', string> = {
  workspace: 'members of the workspace it is used in',
  company: 'members of every workspace the Company links, including this one',
  any: 'anyone, including people outside the Company',
};

/**
 * ADR-023: the Company alone decides which arches send as it, and a `company`
 * or `any` reach lets mail written in another workspace land with this one's
 * members. The people responsible for this workspace are told what can reach
 * them, even though the setting lives with the Company.
 */
function CompanyEmailAppsDisclosure({
  companyName,
  apps,
}: {
  companyName: string;
  apps: NonNullable<WorkspaceCompanyDto['emailApps']>;
}) {
  return (
    <div className="mt-3 space-y-1.5 border-t pt-3">
      <p className="font-medium text-foreground">Arches that email as {companyName}</p>
      <ul className="space-y-1">
        {apps.map((a) => (
          <li key={a.archeId} className="text-muted-foreground">
            <span className="text-foreground">{a.archeName ?? a.archeId}</span> sends as{' '}
            {a.fromAddress} to {EMAIL_REACH[a.recipientScope]}.
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">
        The Company sets this in its email settings. Anyone who gets these emails can stop them with
        one click.
      </p>
    </div>
  );
}

export function CompanyCard({ holderId, isOwner }: { holderId: string; isOwner: boolean }) {
  const query = useWorkspaceCompany(holderId);
  const data = query.data;

  return (
    <Card id={WORKSPACE_COMPANY_CARD_ID}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" aria-hidden="true" />
          Company
        </CardTitle>
        <CardDescription>
          A Company unifies billing, administration and arche approvals across the workspaces it
          links. Linking leaves this workspace&apos;s data as it is; its roster changes only if an
          owner chooses to add the Company&apos;s owners as admins.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : data?.company ? (
          <div className="text-sm">
            <p className="text-foreground">
              This workspace belongs to <span className="font-medium">{data.company.name}</span>.
            </p>
            <p className="text-muted-foreground">
              Its usage bills to the Company&apos;s payer. Only the Company can unlink it; if that
              is ever wrong, contact support.
            </p>
            {data.emailApps && data.emailApps.length > 0 && (
              <CompanyEmailAppsDisclosure companyName={data.company.name} apps={data.emailApps} />
            )}
          </div>
        ) : data && data.pendingLinks.length > 0 ? (
          // Several Companies can each hold one open proposal; every one is shown.
          data.pendingLinks.map((p) => (
            <CompanyLinkProposalPanel key={p.invitationId} holderId={holderId} proposal={p} />
          ))
        ) : (
          <p className="text-sm text-muted-foreground">
            Not linked to a Company.{' '}
            {isOwner ? 'A Company can invite this workspace; you accept it here.' : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function ApprovalsCard({ holderId, canManage }: { holderId: string; canManage: boolean }) {
  const approvals = useWorkspaceApprovals(holderId);
  const decide = useDecideWorkspaceApproval(holderId);
  const revoke = useRevokeWorkspaceApproval(holderId);
  const denials = useWorkspaceAccessDenials(canManage ? holderId : null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // A denied row's Approve re-opens the disclosure on that arche.
  const [seedArcheId, setSeedArcheId] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Arche approvals</CardTitle>
        <CardDescription>
          Arches approved here can read this workspace&apos;s data on behalf of members — at each
          member&apos;s own role, never more — without picking documents one by one. A denial here
          beats an approval from the Company. Revoking is immediate. Built-in Fias features (Todo
          Lists, Calendar, …) follow workspace roles and need no approval.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {canManage && (
          <Button
            size="sm"
            onClick={() => {
              setSeedArcheId(null);
              setDialogOpen(true);
            }}
          >
            Approve an arche…
          </Button>
        )}
        <ApprovalsList
          approvals={approvals.data?.approvals ?? []}
          isLoading={approvals.isLoading}
          canManage={canManage}
          accessLink={(archeId) => appAccessSectionPath(holderId, archeId, 'people')}
          fiasTrusted={approvals.data?.fiasTrusted ?? false}
          fiasTrustedArches={approvals.data?.fiasTrustedArches ?? []}
          revokableOrigin="workspace"
          viewerRole={approvals.data?.myRole}
          onRevoke={(id) => revoke.mutateAsync(id)}
          onDeny={(archeId) => decide.mutateAsync({ archeId, decision: 'deny' })}
          onApprove={(archeId) => {
            setSeedArcheId(archeId);
            setDialogOpen(true);
          }}
          activitySource={{
            kind: 'members',
            load: async (archeId) =>
              (await api.getWorkspaceApprovalActivity(holderId, archeId)).activity,
          }}
        />
        {canManage && (denials.data?.length ?? 0) > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">
              Refused access attempts ({denials.data!.length})
            </summary>
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              {denials.data!.slice(0, 50).map((d) => (
                <li key={d.denialId}>
                  {new Date(d.createdAt).toLocaleString()} · {d.reason} · {d.verb ?? d.entityId}
                  {d.archeId ? ` · arche …${d.archeId.slice(-6)}` : ''} · principal …
                  {d.principalId.slice(-6)}
                </li>
              ))}
            </ul>
          </details>
        )}
        <ArcheApprovalDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          scopeKind="workspace"
          scopeLabel="this workspace"
          initialArcheId={seedArcheId}
          loadDisclosure={async (archeId) =>
            (await api.getWorkspaceApprovalDisclosure(holderId, archeId)).disclosure
          }
          searchArches={(q) => api.searchWorkspaceApprovalArches(holderId, q)}
          scopeKey={holderId}
          onDecide={(input) => decide.mutateAsync(input)}
          roleSetup={{ holderId, viewerIsOwner: approvals.data?.myRole === 'owner' }}
        />
      </CardContent>
    </Card>
  );
}
